import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import rosesApi from '../api/rosesApi';
import { RosePack, RoseTransaction, LoadingStatus } from '../../types';

interface RosesState {
  balance: number;
  packs: RosePack[];
  history: RoseTransaction[];
  status: LoadingStatus;
  error: string | null;
}

const initialState: RosesState = {
  balance: 0,
  packs: [],
  history: [],
  status: 'idle',
  error: null,
};

export const fetchRoseBalance = createAsyncThunk(
  'roses/fetchBalance',
  async (_, { rejectWithValue }) => {
    try {
      const res = await rosesApi.getBalance();
      return res.data as { balance: number };
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to load rose balance');
    }
  },
);

export const fetchRosePacks = createAsyncThunk(
  'roses/fetchPacks',
  async (_, { rejectWithValue }) => {
    try {
      const res = await rosesApi.getPacks();
      return res.data as RosePack[];
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to load rose packs');
    }
  },
);

export const fetchRoseHistory = createAsyncThunk(
  'roses/fetchHistory',
  async (cursor: string | undefined, { rejectWithValue }) => {
    try {
      const res = await rosesApi.getHistory(cursor);
      return res.data as RoseTransaction[];
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to load rose history');
    }
  },
);

export const sendRose = createAsyncThunk(
  'roses/send',
  async (toUserId: string, { rejectWithValue }) => {
    try {
      const res = await rosesApi.sendRose(toUserId);
      return res.data as { roseBalance: number };
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to send rose');
    }
  },
);

const rosesSlice = createSlice({
  name: 'roses',
  initialState,
  reducers: {
    setBalance(state, { payload }: { payload: number }) {
      state.balance = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoseBalance.fulfilled, (state, { payload }) => {
        state.balance = payload.balance;
      })
      .addCase(fetchRosePacks.fulfilled, (state, { payload }) => {
        state.packs = payload;
      })
      .addCase(fetchRoseHistory.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchRoseHistory.fulfilled, (state, { payload }) => {
        state.history = payload;
        state.status = 'succeeded';
      })
      .addCase(fetchRoseHistory.rejected, (state, { payload }) => {
        state.status = 'failed';
        state.error = payload as string;
      })
      .addCase(sendRose.fulfilled, (state, { payload }) => {
        state.balance = payload.roseBalance;
      });
  },
});

export const { setBalance } = rosesSlice.actions;
export default rosesSlice.reducer;

export const selectRoseBalance = (state: { roses: RosesState }) => state.roses.balance;
export const selectRosePacks = (state: { roses: RosesState }) => state.roses.packs;
export const selectRoseHistory = (state: { roses: RosesState }) => state.roses.history;
export const selectRosesStatus = (state: { roses: RosesState }) => state.roses.status;
